import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  ADMIN_FORM_PANEL_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';
import Profile from "../../../assets/img/User.png";

const RestrictionDetail = () => {
  const { restrictionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [restriction, setRestriction] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Row actions state for tables
  const [fraudCaseRowActionsOpen, setFraudCaseRowActionsOpen] = useState({});
  const [moderationCaseRowActionsOpen, setModerationCaseRowActionsOpen] = useState({});
  const [incidentRowActionsOpen, setIncidentRowActionsOpen] = useState({});
  const fraudCaseRowActionsRefs = useRef({});
  const moderationCaseRowActionsRefs = useRef({});
  const incidentRowActionsRefs = useRef({});
  
  // Toggle row actions
  const toggleFraudCaseRowActions = (caseId) => {
    setFraudCaseRowActionsOpen((prev) => {
      if (prev[caseId]) return {};
      return { [caseId]: true };
    });
  };
  
  const toggleModerationCaseRowActions = (caseId) => {
    setModerationCaseRowActionsOpen((prev) => {
      if (prev[caseId]) return {};
      return { [caseId]: true };
    });
  };
  
  const toggleIncidentRowActions = (incidentId) => {
    setIncidentRowActionsOpen((prev) => {
      if (prev[incidentId]) return {};
      return { [incidentId]: true };
    });
  };
  
  // Handle clicking outside to close row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const allRefs = [
        ...Object.values(fraudCaseRowActionsRefs.current),
        ...Object.values(moderationCaseRowActionsRefs.current),
        ...Object.values(incidentRowActionsRefs.current)
      ];
      
      const clickedOutside = allRefs.every(ref => {
        const el = ref;
        return !el || !el.contains(event.target);
      });
      
      if (clickedOutside) {
        setFraudCaseRowActionsOpen({});
        setModerationCaseRowActionsOpen({});
        setIncidentRowActionsOpen({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Format date and time
  const formatDateTime = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) + ' · ' + 
           d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  // Format date only
  const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Load restriction data
  useEffect(() => {
    const loadRestriction = async () => {
      setLoading(true);
      try {
        // Get from location state or fetch
        if (location.state?.restriction) {
          setRestriction(location.state.restriction);
        } else {
          // TODO: Fetch from API
          await new Promise(resolve => setTimeout(resolve, 500));
          // Mock data for now
          setRestriction({
            restrictionId: restrictionId,
            accountId: 'C-1177',
            accountName: 'Mark Jones',
            accountType: 'Customer',
            profilePicture: 'https://i.pravatar.cc/150?img=1',
            restrictionType: 'Messaging Blocked',
            reason: 'Messaging scam / spam',
            source: 'Fraud Alerts (FR-000421)',
            appliedBy: 'Sarah Nguyen',
            startDate: new Date(),
            reviewDate: null,
            status: 'Active'
          });
        }
      } catch (error) {
        console.error('Error loading restriction:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRestriction();
  }, [restrictionId, location.state]);

  if (loading || !restriction) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading restriction details…</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          {/* Page Header */}
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-lightgray flex items-center justify-center">
                  {restriction.profilePicture ? (
                    <img 
                      src={restriction.profilePicture}
                      alt={restriction.accountName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = Profile;
                      }}
                    />
                  ) : (
                    <i className="fi fi-rr-user text-2xl text-offblack"></i>
                  )}
                </div>
                <div className="flex-1">
                  {/* Line 1 */}
                  <AdminPageTitle>
                    {restriction.restrictionId} · {restriction.restrictionType} — {restriction.accountType} {restriction.accountId} · {restriction.accountName}
                  </AdminPageTitle>
                  {/* Line 2 (subline) */}
                  <AdminSupportingText>
                    {restriction.status} · {restriction.reason} · Applied by {restriction.appliedBy} · {formatDate(restriction.startDate)} · Review: {restriction.reviewDate ? formatDate(restriction.reviewDate) : 'Indefinite'}
                  </AdminSupportingText>
                </div>
              </div>
            </div>
          </DashboardMainContainer>

          {/* Section 1: Restriction Summary & Actions */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              {/* Restriction Summary - Full Width with Columns */}
              <div>
                <AdminPanelTitle className="text-offblack">Restriction Summary</AdminPanelTitle>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Restriction ID</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">{restriction.restrictionId}</div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Account</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">
                      {restriction.accountType} {restriction.accountId} · {restriction.accountName}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Account Type</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">{restriction.accountType}</div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Restriction Type</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">{restriction.restrictionType}</div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Reason</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">{restriction.reason}</div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Source</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">
                      {restriction.source.includes('FR-') ? (
                        <>
                          <span>Fraud Alerts — </span>
                          <AdminLinkAction
                            type="button"
                            onClick={() => navigate(`/admin/fraud/alerts/${restriction.source.match(/FR-\d+/)?.[0]}`)}
                          >
                            {restriction.source.match(/FR-\d+/)?.[0]}
                          </AdminLinkAction>
                        </>
                      ) : restriction.source.includes('MC-') ? (
                        <>
                          <span>Moderation — </span>
                          <AdminLinkAction
                            type="button"
                            onClick={() => navigate(`/admin/moderation/queue?caseId=${restriction.source.match(/MC-\d+/)?.[0]}`)}
                          >
                            {restriction.source.match(/MC-\d+/)?.[0]}
                          </AdminLinkAction>
                        </>
                      ) : restriction.source.includes('INC-') ? (
                        <>
                          <span>Security Incident — </span>
                          <AdminLinkAction
                            type="button"
                            onClick={() => navigate(`/admin/fraud/incidents/${restriction.source.match(/INC-\d+/)?.[0]}`)}
                          >
                            {restriction.source.match(/INC-\d+/)?.[0]}
                          </AdminLinkAction>
                        </>
                      ) : (
                        restriction.source
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Applied By</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">{restriction.appliedBy}</div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Start Date</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">{formatDateTime(restriction.startDate)}</div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Review / End Date</AdminDetailFieldLabel>
                    <div className="text-sm text-offblack font-medium">
                      {restriction.reviewDate ? formatDateTime(restriction.reviewDate) : 'Indefinite'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                    <div>
                      <AdminStatusBadge status={restriction.status} label={restriction.status} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions - Second Row */}
              <div className="mt-6 pt-6 border-t border-lightgray">
                <AdminPanelTitle className="text-offblack">Quick Actions</AdminPanelTitle>
                <div className="flex flex-wrap gap-3">
                  <AdminHeaderAction
                    onClick={() => {
                      // TODO: Open modify panel
                      console.log('Modify restriction');
                    }}
                  >
                    Modify Restriction
                  </AdminHeaderAction>
                  <AdminHeaderAction
                    onClick={() => {
                      if (window.confirm(`Lift ${restriction.restrictionType} for ${restriction.accountId}? This will immediately allow access again.`)) {
                        // TODO: Lift restriction
                        console.log('Lift restriction');
                      }
                    }}
                  >
                    Lift Restriction
                  </AdminHeaderAction>
                  <AdminHeaderAction
                    onClick={() => {
                      // TODO: Open extend date panel
                      console.log('Extend / Set Review Date');
                    }}
                  >
                    Extend / Set Review Date
                  </AdminHeaderAction>
                  <AdminHeaderAction
                    onClick={() => {
                      if (restriction.accountType === 'Customer') {
                        navigate(`/admin/users/customers/${restriction.accountId}`);
                      } else {
                        navigate(`/admin/users/businesses/${restriction.accountId}`);
                      }
                    }}
                  >
                    Open Account Profile
                  </AdminHeaderAction>
                </div>
              </div>
          </DashboardMainContainer>
          
          {/* Section 2: Account Snapshot */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <AdminPanelTitle className="text-offblack">Account Snapshot</AdminPanelTitle>
              {restriction.accountType === 'Customer' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Customer</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {restriction.accountId} · {restriction.accountName}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Email</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">mark.jones@example.com</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Mobile</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">Not Provided</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                      <div>
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-orange-500 text-white">
                          Restricted
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Joined</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">20 Aug 2024</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Location</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">Sydney, NSW</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Fraud Risk Level</AdminDetailFieldLabel>
                      <div>
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-alertred text-white">
                          High
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-lightgray pt-4">
                    <AdminDetailFieldLabel>Other Active Restrictions</AdminDetailFieldLabel>
                    <div className="space-y-2">
                      <div className="text-sm text-offblack font-medium">
                        {restriction.restrictionId} · {restriction.restrictionType} (this)
                      </div>
                      <div className="text-sm text-offblack font-medium">
                        R-0090 · Temporary Suspension (expired)
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-lightgray pt-4">
                    <div className="flex gap-3">
                        <AdminHeaderAction
                        onClick={() => navigate(`/admin/users/customers/${restriction.accountId}`)}
                      >
                        View Customer Profile
                      </AdminHeaderAction>
                        <AdminHeaderAction
                        onClick={() => navigate(`/admin/fraud/alerts?accountId=${restriction.accountId}`)}
                      >
                        Open Fraud Cases
                      </AdminHeaderAction>
                        <AdminHeaderAction
                        onClick={() => navigate(`/admin/moderation/queue?accountId=${restriction.accountId}`)}
                      >
                        Open Moderation Cases
                      </AdminHeaderAction>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Business</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {restriction.accountId} · {restriction.accountName}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>ABN</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">12345678901</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Plan</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">Pro (Monthly)</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Verification</AdminDetailFieldLabel>
                      <div>
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-freshgreen text-white">
                          Verified
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                      <div>
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-orange-500 text-white">
                          Restricted
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-lightgray pt-4">
                    <div className="flex gap-3">
                        <AdminHeaderAction
                        onClick={() => navigate(`/admin/users/businesses/${restriction.accountId}`)}
                      >
                        View Business Profile
                      </AdminHeaderAction>
                        <AdminHeaderAction
                        onClick={() => navigate(`/admin/fraud/alerts?accountId=${restriction.accountId}`)}
                      >
                        Open Fraud Cases
                      </AdminHeaderAction>
                        <AdminHeaderAction
                        onClick={() => navigate(`/admin/moderation/queue?accountId=${restriction.accountId}`)}
                      >
                        Open Moderation Cases
                      </AdminHeaderAction>
                    </div>
                  </div>
                </div>
              )}
          </DashboardMainContainer>
          
          {/* Section 3: Restriction Scope & Effect */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <AdminPanelTitle className="text-offblack">Restriction Scope & Effect</AdminPanelTitle>
              <div className="space-y-6">
                <div>
                  <AdminPanelTitle className="text-offblack">
                    Restriction Type: {restriction.restrictionType} ({restriction.accountType} Example)
                  </AdminPanelTitle>
                  <div className="space-y-4">
                    <div>
                      <AdminDetailFieldLabel>Scope of effect</AdminDetailFieldLabel>
                    {restriction.restrictionType === 'Messaging Blocked' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>{restriction.accountType}</strong> cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Start new message threads</li>
                          <li className="text-sm text-offblack font-medium">Reply to existing messages</li>
                          <li className="text-sm text-offblack font-medium">Send attachments via messaging</li>
                        </ul>
                        <div className="text-sm text-offblack font-medium mt-2">
                          <strong>Business behaviour:</strong> Business can still reply to existing quotes through quote updates (if you allow), but not via free-form chat if messaging is blocked globally.
                        </div>
                        <div className="text-sm text-offblack font-medium mt-2 italic">
                          UI example ({restriction.accountType} side): "Your messaging access has been temporarily restricted. Please contact support if you believe this is an error."
                        </div>
                      </div>
                    )}
                    {restriction.restrictionType === 'Posting Blocked' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>Business</strong> cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Create new posts</li>
                          <li className="text-sm text-offblack font-medium">Duplicate or relist posts</li>
                          <li className="text-sm text-offblack font-medium">Renew expired posts</li>
                        </ul>
                        <div className="text-sm text-offblack font-medium mt-2">
                          <strong>Existing posts:</strong> Can be set to Hidden or Remain live but no new leads (depending on product decision).
                        </div>
                      </div>
                    )}
                    {restriction.restrictionType === 'Login Blocked' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>{restriction.accountType}</strong> cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Access their account</li>
                          <li className="text-sm text-offblack font-medium">Login to the platform</li>
                          <li className="text-sm text-offblack font-medium">Perform any account actions</li>
                        </ul>
                      </div>
                    )}
                    {restriction.restrictionType === 'Quotes Blocked' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>{restriction.accountType}</strong> cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Initiate new quotes</li>
                          <li className="text-sm text-offblack font-medium">Respond to existing quotes</li>
                        </ul>
                      </div>
                    )}
                    {restriction.restrictionType === 'Payments Blocked' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>Business</strong> cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Upgrade subscription</li>
                          <li className="text-sm text-offblack font-medium">Renew subscription</li>
                          <li className="text-sm text-offblack font-medium">Process new charges</li>
                        </ul>
                      </div>
                    )}
                    {restriction.restrictionType === 'Temporary Suspension' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>{restriction.accountType}</strong> cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Access most platform features</li>
                          <li className="text-sm text-offblack font-medium">Create new content</li>
                          <li className="text-sm text-offblack font-medium">Interact with other users</li>
                        </ul>
                      </div>
                    )}
                    {restriction.restrictionType === 'Permanent Ban' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>{restriction.accountType}</strong> is permanently banned and cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Access the platform</li>
                          <li className="text-sm text-offblack font-medium">Create new accounts</li>
                          <li className="text-sm text-offblack font-medium">Use any platform features</li>
                        </ul>
                      </div>
                    )}
                    {restriction.restrictionType === 'Visibility Hidden' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>Business</strong> profile and posts are hidden from public view:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Profile not visible in search</li>
                          <li className="text-sm text-offblack font-medium">Posts hidden from public</li>
                          <li className="text-sm text-offblack font-medium">Cannot receive new leads</li>
                        </ul>
                      </div>
                    )}
                    {restriction.restrictionType === 'Badges Hidden' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>Business</strong> badges are hidden:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Verified badges not displayed</li>
                          <li className="text-sm text-offblack font-medium">Badge status hidden from public</li>
                        </ul>
                      </div>
                    )}
                    {restriction.restrictionType === 'Verification Locked' && (
                      <div className="space-y-2 pl-4">
                        <div className="text-sm text-offblack font-medium">
                          <strong>Business</strong> cannot:
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-4">
                          <li className="text-sm text-offblack font-medium">Submit new verification documents</li>
                          <li className="text-sm text-offblack font-medium">Update verification status</li>
                          <li className="text-sm text-offblack font-medium">Change badge status</li>
                        </ul>
                      </div>
                    )}
                  </div>
                    <div className="pt-4 border-t border-lightgray">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Applies To</AdminDetailFieldLabel>
                          <div className="text-sm text-offblack font-medium">
                            {restriction.restrictionType === 'Messaging Blocked' ? 'New + replies (default)' : 'All applicable features'}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Related Features Impacted</AdminDetailFieldLabel>
                          <div className="text-sm text-offblack font-medium">
                            {restriction.restrictionType === 'Messaging Blocked' ? 'Quote messaging, Direct messaging from posts' : 'Platform-wide'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          </DashboardMainContainer>
          
          {/* Section 4: Linked Cases & Source Context */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <AdminPanelTitle className="text-offblack">Linked Cases & Source Context</AdminPanelTitle>
              <div className="space-y-8">
                {/* Linked Fraud Cases */}
                <div>
                  <AdminPanelTitle className="text-offblack">Linked Fraud Cases</AdminPanelTitle>
                  <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                    <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(100px, 1fr)' }}>
                      <div className="flex items-center"><span className="font-semibold">Case ID</span></div>
                      <div className="flex items-center"><span className="font-semibold">Alert Type</span></div>
                      <div className="flex items-center"><span className="font-semibold">Risk</span></div>
                      <div className="flex items-center"><span className="font-semibold">Status</span></div>
                      <div className="flex items-center"><span className="font-semibold">Actions</span></div>
                    </div>
                    <div className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(100px, 1fr)' }}>
                      <div className="flex items-center">
                        <AdminLinkAction
                          type="button"
                          onClick={() => navigate('/admin/fraud/alerts/FR-000421')}
                        >
                          FR-000421
                        </AdminLinkAction>
                      </div>
                      <div className="flex items-center">
                        <span className="">Multiple Accounts – Same Device</span>
                      </div>
                      <div className="flex items-center">
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-alertred text-white">
                          High
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-blue-500 text-white">
                          In Progress
                        </span>
                      </div>
                      <div className="flex items-center relative">
                        <div className="relative" ref={el => fraudCaseRowActionsRefs.current['FR-000421'] = el}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFraudCaseRowActions('FR-000421');
                            }}
                            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Row Actions"
                          >
                            <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                          </button>
                          {fraudCaseRowActionsOpen['FR-000421'] && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[150px] shadow-lg border border-lightgray">
                              <div className="py-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/admin/fraud/alerts/FR-000421');
                                    setFraudCaseRowActionsOpen({});
                                  }}
                                  className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                >
                                  <AdminSupportingText>Open Case</AdminSupportingText>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </DashboardMainContainer>
                </div>

                {/* Linked Moderation Cases */}
                <div>
                  <AdminPanelTitle className="text-offblack">Linked Moderation Cases / Flags</AdminPanelTitle>
                  <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                    <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(100px, 1fr) minmax(100px, 1fr)' }}>
                      <div className="flex items-center"><span className="font-semibold">ID</span></div>
                      <div className="flex items-center"><span className="font-semibold">Type</span></div>
                      <div className="flex items-center"><span className="font-semibold">Target</span></div>
                      <div className="flex items-center"><span className="font-semibold">Status</span></div>
                      <div className="flex items-center"><span className="font-semibold">Severity</span></div>
                      <div className="flex items-center"><span className="font-semibold">Actions</span></div>
                    </div>
                    <div className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(100px, 1fr) minmax(100px, 1fr)' }}>
                      <div className="flex items-center">
                        <span className="">MC-00421</span>
                      </div>
                      <div className="flex items-center">
                        <span className="">Moderation Case</span>
                      </div>
                      <div className="flex items-center">
                        <span className="">REV-2210</span>
                      </div>
                      <div className="flex items-center">
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-blue-500 text-white">
                          In Progress
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-alertred text-white">
                          High
                        </span>
                      </div>
                      <div className="flex items-center relative">
                        <div className="relative" ref={el => moderationCaseRowActionsRefs.current['MC-00421'] = el}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleModerationCaseRowActions('MC-00421');
                            }}
                            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Row Actions"
                          >
                            <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                          </button>
                          {moderationCaseRowActionsOpen['MC-00421'] && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[150px] shadow-lg border border-lightgray">
                              <div className="py-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/admin/moderation/queue?caseId=MC-00421');
                                    setModerationCaseRowActionsOpen({});
                                  }}
                                  className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                >
                                  <AdminSupportingText>Open Case</AdminSupportingText>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </DashboardMainContainer>
                </div>

                {/* Linked Security Incidents */}
                {restriction.source.includes('INC-') && (
                  <div>
                    <AdminPanelTitle className="text-offblack">Linked Security Incidents</AdminPanelTitle>
                    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                      <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(100px, 1fr)' }}>
                        <div className="flex items-center"><span className="font-semibold">Incident ID</span></div>
                        <div className="flex items-center"><span className="font-semibold">Type</span></div>
                        <div className="flex items-center"><span className="font-semibold">Severity</span></div>
                        <div className="flex items-center"><span className="font-semibold">Status</span></div>
                        <div className="flex items-center"><span className="font-semibold">Actions</span></div>
                      </div>
                      <div className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(100px, 1fr)' }}>
                        <div className="flex items-center">
                          <span className="">INC-0012</span>
                        </div>
                        <div className="flex items-center">
                          <span className="">Payment Fraud</span>
                        </div>
                        <div className="flex items-center">
                          <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-alertred text-white">
                            Critical
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className=" px-2 py-1 rounded-full text-xs whitespace-nowrap bg-orange-500 text-white">
                            Investigating
                          </span>
                        </div>
                        <div className="flex items-center relative">
                          <div className="relative" ref={el => incidentRowActionsRefs.current['INC-0012'] = el}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleIncidentRowActions('INC-0012');
                              }}
                              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                              title="Row Actions"
                            >
                              <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                            </button>
                            {incidentRowActionsOpen['INC-0012'] && (
                              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[150px] shadow-lg border border-lightgray">
                                <div className="py-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/admin/fraud/incidents/INC-0012');
                                      setIncidentRowActionsOpen({});
                                    }}
                                    className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                  >
                                    <AdminSupportingText>Open Incident</AdminSupportingText>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </DashboardMainContainer>
                  </div>
                )}
              </div>
          </DashboardMainContainer>
          
          {/* Section 5: Restriction Timeline */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <AdminPanelTitle className="text-offblack">Restriction Timeline</AdminPanelTitle>
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(160px, 1.8fr) minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(250px, 2.5fr) minmax(150px, 1.5fr)' }}>
                  <div className="flex items-center"><span className="font-semibold">Timestamp</span></div>
                  <div className="flex items-center"><span className="font-semibold">Actor</span></div>
                  <div className="flex items-center"><span className="font-semibold">Event</span></div>
                  <div className="flex items-center"><span className="font-semibold">Detail / Change</span></div>
                  <div className="flex items-center"><span className="font-semibold">Source</span></div>
                </div>
                <div className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(160px, 1.8fr) minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(250px, 2.5fr) minmax(150px, 1.5fr)' }}>
                  <div className="flex items-center">
                    <span className="">{formatDateTime(restriction.startDate)}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="">{restriction.appliedBy}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="">Restriction Applied</span>
                  </div>
                  <div className="flex items-center">
                    <span className="">
                      {restriction.restrictionType} (Reason: {restriction.reason})
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="">{restriction.source}</span>
                  </div>
                </div>
                <div className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(160px, 1.8fr) minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(250px, 2.5fr) minmax(150px, 1.5fr)' }}>
                  <div className="flex items-center">
                    <span className="">
                      {formatDateTime(new Date(new Date(restriction.startDate).getTime() + 24 * 60 * 60 * 1000))}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="">System</span>
                  </div>
                  <div className="flex items-center">
                    <span className="">Reminder</span>
                  </div>
                  <div className="flex items-center">
                    <span className="">Restriction has been active for 24 hours</span>
                  </div>
                  <div className="flex items-center">
                    <span className="">System</span>
                  </div>
                </div>
                {restriction.reviewDate && (
                  <div className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(160px, 1.8fr) minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(250px, 2.5fr) minmax(150px, 1.5fr)' }}>
                    <div className="flex items-center">
                      <span className="">
                        {formatDateTime(new Date(new Date(restriction.reviewDate).getTime() - 10 * 24 * 60 * 60 * 1000))}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="">David Lee</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">Review Date Updated</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">
                        Review Date set to {formatDate(restriction.reviewDate)}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="">Manual Admin</span>
                    </div>
                  </div>
                )}
                {restriction.status === 'Expired' && (
                  <div className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(160px, 1.8fr) minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(250px, 2.5fr) minmax(150px, 1.5fr)' }}>
                    <div className="flex items-center">
                      <span className="">
                        {formatDateTime(restriction.reviewDate ? new Date(restriction.reviewDate) : new Date())}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="">System</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">Status Changed</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">Active → Expired</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">System</span>
                    </div>
                  </div>
                )}
              </DashboardMainContainer>
          </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RestrictionDetail;

